import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import productService from '../../services/product';
import restProductService from '../../services/rest/product';

const initialState = {
  loading: false,
  products: [],
  error: '',
  params: {
    page: 1,
    perPage: 10,
  },
  links: null,
  meta: {},
  form: {
    // initially fetched stocks
    stocks: [],
    // filtered stocks
    filteredStocks: [],
    // holds filter values
    filters: {},
    // deleted ids in filter type
    deletedIds: [],
  },
};

export const fetchProducts = createAsyncThunk(
  'product/fetchProducts',
  (params = {}) => {
    return productService
      .getAll({ ...initialState.params, ...params })
      .then((res) => res);
  },
);

export const fetchRestProducts = createAsyncThunk(
  'product/fetchRestProducts',
  (params = {}) => {
    return restProductService
      .getAll({ ...initialState.params, ...params })
      .then((res) => res);
  },
);

const productSlice = createSlice({
  name: 'product',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchProducts.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchProducts.fulfilled, (state, action) => {
      const { payload } = action;
      state.loading = false;
      const raw = payload?.data;
      const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
      state.products = list.map((item) => ({
        ...item,
        id: item.id ?? item._id,
        _id: item._id ?? item.id,
        uuid: item.uuid,
        name: item.translation ? item.translation.title : item.title ?? 'no name',
        active: item.active,
        // A variable product has no photo of its own; the API sends its
        // default variant's first photo as list_thumbnail.
        img: item.thumbnail || item.images?.[0] || item.img || item.list_thumbnail || null,
        productType: item.product_type ?? (item.hasVariants ? 'variable' : 'simple'),
        basePrice: item.basePrice,
        salePrice: item.salePrice,
        stockQuantity: item.stockQuantity,
        status: item.status,
        variants: item.variants,
        isDigital: item.isDigital,
        digitalFileUrl: item.digitalFileUrl,
        category_name: item.category?.translation
          ? item.category.translation.title
          : item.category?.title ?? 'no name',
        // Admin productSerializer — preserve storefront pin fields for list/detail UI
        sectionSort: item.sectionSort ?? item.section_sort,
        section_sort: item.section_sort ?? item.sectionSort,
        currentOffersSort: item.currentOffersSort ?? item.current_offers_sort,
        current_offers_sort: item.current_offers_sort ?? item.currentOffersSort,
        mostSoldSort: item.mostSoldSort ?? item.most_sold_sort,
        most_sold_sort: item.most_sold_sort ?? item.mostSoldSort,
      }));
      const meta = payload?.meta ?? payload?.pagination ?? {};
      state.meta = meta;
      state.links = payload?.links;
      state.params.page = meta.current_page ?? meta.page ?? state.params.page;
      state.params.perPage = meta.per_page ?? state.params.perPage;
      state.error = '';
    });
    builder.addCase(fetchProducts.rejected, (state, action) => {
      state.loading = false;
      state.products = [];
      state.error = action.error.message;
    });

    //rest products
    builder.addCase(fetchRestProducts.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchRestProducts.fulfilled, (state, action) => {
      const { payload } = action;
      state.loading = false;
      const list = payload?.data ?? [];
      state.products = list.map((item) => {
        const product = item.product ?? item;
        return {
          ...item,
          id: item.id ?? product?.id,
          uuid: item.uuid ?? product?.uuid,
          name: product?.translation?.title ?? product?.title ?? item.title ?? 'no name',
          active: item.active ?? product?.active,
          img: item.thumbnail ?? item.images?.[0] ?? item.img ?? product?.thumbnail ?? product?.images?.[0],
          final_price: item.final_price,
          base_price: item.base_price,
          sale_price: item.sale_price,
          stock_quantity: item.stock_quantity,
          has_variants: item.has_variants,
          variants: item.variants,
          is_digital: item.is_digital,
          digital_file_url: item.digital_file_url,
          category_name: item.category?.title ?? product?.category?.title ?? 'no name',
          unit: item?.unit ?? product?.unit,
        };
      });
      const meta = payload?.meta ?? {};
      state.meta = meta;
      state.links = payload?.links;
      state.params.page = meta.current_page ?? state.params.page;
      state.params.perPage = meta.per_page ?? state.params.perPage;
      state.error = '';
    });
    builder.addCase(fetchRestProducts.rejected, (state, action) => {
      state.loading = false;
      state.products = [];
      state.error = action.error.message;
    });
  },
  reducers: {
    setFormStocks(state, action) {
      state.form.stocks = action.payload;
    },
    updateFormStocks(state, action) {
      state.form.stocks = state.form.stocks.map((stock) => {
        const updatedStock = action?.payload?.find(
          (item) => item?.fieldKey === stock?.fieldKey,
        );
        if (updatedStock) {
          return updatedStock;
        }
        return stock;
      });
    },
    resetFormStocks(state) {
      state.form.stocks = initialState.form.stocks;
    },
    setFilteredStocks(state, action) {
      state.form.filteredStocks = action.payload;
    },
    resetFilteredStocks(state) {
      state.form.filteredStocks = initialState.form.filteredStocks;
    },
    setFilters(state, action) {
      state.form.filters = { ...state.form.filters, ...action.payload };
    },
    deleteFilter(state, action) {
      delete state.form.filters[action.payload];
    },
    resetFilters(state) {
      state.form.filters = initialState.form.filters;
    },
    deleteStockFromStocks(state, action) {
      state.form.stocks = state.form?.stocks?.filter((stock) => {
        const isEqual = stock?.fieldKey !== action.payload?.fieldKey;
        if (!isEqual && stock?.stock_id) {
          state.form.deletedIds.push(stock?.stock_id);
        }
        return isEqual;
      });
    },
    resetDeletedIds(state) {
      state.form.deletedIds = initialState.form.deletedIds;
    },
  },
});

export const {
  setFormStocks,
  updateFormStocks,
  resetFormStocks,
  setFilteredStocks,
  resetFilteredStocks,
  setFilters,
  resetFilters,
  deleteFilter,
  deleteStockFromStocks,
  resetDeletedIds,
} = productSlice.actions;

export default productSlice.reducer;
